/**
 * Null tolerance for Brand Portfolio client fields.
 *
 * In enforce mode the backend may answer `null` for fields whose package feature the agency lacks (health,
 * velocity, runway, channel health, relationship). The UI gates normally decide from `lockedFeatures`, but a
 * missing flag must never crash a reader or print NaN / "0 days". So the mapper normalises every nullable field
 * to a NEUTRAL typed default here, and reports which feature keys were redacted so the provider can lock them
 * even when `lockedFeatures` does not say so.
 */
import type {
  ChannelHealthData,
  HealthDimension,
  HealthTier,
  RenewalData,
  VelocityData,
} from '@/data/portfolio.types';

export interface NullableRedactableFields {
  healthOverall?: HealthTier | null;
  healthOperational?: HealthDimension | null;
  healthBrandEquity?: HealthDimension | null;
  healthCommercial?: HealthDimension | null;
  weeklyScoreHistory?: number[] | null;
  velocity?: VelocityData | null;
  runwayDays?: number | null;
  coverDays?: number | null;
  runsDryOn?: string | null;
  lowPipeline?: boolean | null;
  coverBreakdown?: { plannedDays: number; readyDays: number } | null;
  channelHealth?: ChannelHealthData | null;
  renewal?: RenewalData | null;
}

export interface NormalizedRedactableFields {
  healthOverall: HealthTier;
  healthOperational: HealthDimension;
  healthBrandEquity: HealthDimension;
  healthCommercial: HealthDimension;
  weeklyScoreHistory: number[];
  velocity: VelocityData;
  runwayDays: number;
  coverDays: number;
  runsDryOn: string | null;
  lowPipeline: boolean;
  coverBreakdown: { plannedDays: number; readyDays: number };
  channelHealth: ChannelHealthData;
  renewal: RenewalData;
  /** Feature keys whose fields arrived as null (the backend redacted them) */
  redactedFeatures: string[];
}

const EMPTY_DIMENSION: HealthDimension = { score: 0, signals: [] };

export const normalizeRedactableFields = (
  raw: NullableRedactableFields,
  isPausedBrand: boolean,
): NormalizedRedactableFields => {
  const redacted: string[] = [];
  const healthRedacted =
    raw.healthOverall == null || raw.healthOperational == null || raw.healthBrandEquity == null || raw.healthCommercial == null;
  if (healthRedacted) redacted.push('portfolio_health_score');
  if (raw.velocity == null) redacted.push('portfolio_velocity');
  if (raw.runwayDays == null || raw.coverDays == null) redacted.push('portfolio_runway');
  if (raw.channelHealth == null) redacted.push('portfolio_channel_health');
  if (raw.renewal == null) redacted.push('portfolio_client_relationship');

  return {
    // A redacted tier must not move a paused brand into the active group
    healthOverall: raw.healthOverall ?? (isPausedBrand ? 'paused' : 'on_track'),
    healthOperational: raw.healthOperational ?? EMPTY_DIMENSION,
    healthBrandEquity: raw.healthBrandEquity ?? EMPTY_DIMENSION,
    healthCommercial: raw.healthCommercial ?? EMPTY_DIMENSION,
    weeklyScoreHistory: raw.weeklyScoreHistory ?? [],
    velocity: raw.velocity ?? { postsThisWeek: 0, postsPriorWeek: 0, trendPct: 0 },
    runwayDays: raw.runwayDays ?? 0,
    coverDays: raw.coverDays ?? 0,
    runsDryOn: raw.runsDryOn ?? null,
    lowPipeline: raw.lowPipeline ?? false,
    coverBreakdown: raw.coverBreakdown ?? { plannedDays: 0, readyDays: 0 },
    channelHealth: raw.channelHealth ?? { total: 0, broken: 0, expiring: 0 },
    renewal: raw.renewal ?? { daysUntilRenewal: null, atRisk: false },
    redactedFeatures: redacted,
  };
};
